import {httpClient, openWebSocket} from '@api/baseApi';
import {ConfigTypeSpectrumValueInfo, ConfigTypeValueInfo} from './nodeApi';
import {secToMills} from '@/utils/dateTimeUtils';
import {DateTimeInterval} from '@/utils/dateTimeItervalUtils';
import {spectrumValueInfoes} from './configurate';
import authModule from '@/store/modules/authModule';
import {ConfigType, PModuleType} from '@/enums/configs';
import {SpectrumEventType, SpectrumYAxisType} from '@/enums/diagrams';
import {PMActionType} from '@/views/system/modules/enums';

/*Атрибуты программного модуля*/
export interface MainConfig {
  type: ConfigType;
  typeAlias: string;
  id: string;
  name: string;
  node?: string;
  nodeAlias?: string;
  implemented: boolean;
}

/*Атрибуты программного модуля в узле*/
export interface NodeConfig {
  id: string;
  type?: string;
  coords: {x: number; y: number} | null;
  name?: string;
}

/*Ссылка на узел в узле*/
export interface NodeLink {
  id: string;
  type?: string;
  coords: {x: number; y: number} | null;
  name?: string;
}

/**/

/*websocets запросы */

export const wsRouteByPmTypeMap: Map<PModuleType, string> = new Map<PModuleType, string>([
  [PModuleType.CrmModule, 'crm-modules'],
  [PModuleType.DtcpTahoModule, 'dtcp-tachometer-modules'],
  [PModuleType.DtcpAccelerometerModule, 'dtcp-accelerometer-modules'],
  [PModuleType.DtcpServerModule, 'dtcp-server-modules'],
  [PModuleType.DtcpEnergyModule, 'dtcp-energy-modules'],
  [PModuleType.DtcpTemperatureModule, 'dtcp-temperature-modules'],
  [PModuleType.CleanStorageModule, 'clean-storage-modules'],
  [PModuleType.HeaterMonitorModule, 'heater-monitor-modules'],
  [PModuleType.ServerMonitorModule, 'server-monitor-modules'],
  [PModuleType.PingerModule, 'pinger-modules'],
  [PModuleType.DtcpEmulatorModule, 'dtcp-emulator-modules'],
  [PModuleType.BearingControlModule, 'bearing-control-modules'],
]);

export function connectionGetActiveDevices(guid: string) {
  const accessToken = authModule.getAccessToken || '';
  const isRegistered = false;
  const type = 1;
  const action = 'get_active_devices';
  return openWebSocket(
    `modules/actions/get_active_devices/?module_identifier=${guid}&action=${action}&type=${type}&is_registered=${isRegistered}&access_token=${accessToken}`,
  );
}

export function connectionPModuleValue(guid: string, type: string, item: string) {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(
    `${type}/?module_identifier=${guid}&module_alias=${item}&access_token=${accessToken}`,
  );
}

// отдельные сокет для данных ПМ -> лучше использовать общий.
export function connectionPModuleValueNew(
  guid: string,
  type: PModuleType,
  item: string,
  statType = '',
) {
  const accessToken = authModule.getAccessToken || '';
  let wsRoute = `${wsRouteByPmTypeMap.get(
    type,
  )}/?module_identifier=${guid}&module_alias=${item}&access_token=${accessToken}`;
  if (statType) wsRoute += `&event_type=${statType}`;
  return openWebSocket(wsRoute);
}

// Общий сокет с данными для сигналов и спектров. Данные приходят после соотв запросов:
// addSignalSubscription и addScpectrSubscription  в socketUtils
export function connectionPModulesValues() {
  const accessToken = authModule.getAccessToken || '';
  const wsRoute = `modules/data/?access_token=${accessToken}`;
  return openWebSocket(wsRoute);
}

export function connectionPModuleBokerEvents(guids: string[], event_types: string[]) {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(
    `or_pm_manager/modules/events/?module_identifiers=[${guids
      .map(x => `"${x}"`)
      .join(',')}]&event_types=[${event_types
      .map(x => `"${x}"`)
      .join(',')}]&access_token=${accessToken}`,
  );
}

export function connectionPModuleSystemLog(guid: string) {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(
    `or_pm_manager/module/system_log/?module_identifier=${guid}&access_token=${accessToken}`,
  );
}

export function connectionPModuleActions(guid: string, action: PMActionType) {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(
    `modules/actions/?module_identifier=${guid}&action=${action}&access_token=${accessToken}`,
  );
}

export function connectionConfigWorkStatus() {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(`or_pm_manager/statuses/changes/?access_token=${accessToken}`);
}

export function connectionConfigSpectrumValueNew(
  guid: string,
  type: PModuleType,
  eventType: string,
  spectrumType: SpectrumEventType,
) {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(
    `${wsRouteByPmTypeMap.get(
      type,
    )}/spectrum/?module_identifier=${guid}&event_type=${eventType}&spectrum_type=${spectrumType as number}&access_token=${accessToken}`,
  );
}

export function connectionConfigSpectrumValue(guid: string, type: string, spectrumType: string) {
  const accessToken = authModule.getAccessToken || '';
  return openWebSocket(
    `${type}/spectrum/?module_identifier=${guid}&spectrum_type=${spectrumType}&access_token=${accessToken}`,
  );
}

/**/

/*http-запросы*/

/*
Получить данные с ПМ
*/
export async function requestConfigData(
  id: string,
  dateInterval: DateTimeInterval,
  fieldAlias: string,
) {
  const {data} = await httpClient.get(`configs/${id}/data`, {
    params: {
      start_date: dateInterval.startDateTime,
      end_date: dateInterval.endDateTime,
      field_alias: fieldAlias,
      points_to_keep: 1000,
    },
  });
  const bottomBound = data.bottom_bound;
  const topBound = data.top_bound;

  const dataSet: {x: number; y: number}[] = data.data.map((elem: {timestamp: number; data: any}): {
    x: number;
    y: number;
  } => ({
    x: secToMills(elem.timestamp),
    y: elem.data[fieldAlias],
  }));

  return {
    bottomBound,
    topBound,
    dataSet,
  };
}

export function getSpectrumFreqFormatted(
  index: number,
  leftX: number,
  rightX: number,
  dataLength: number,
) {
  return Math.round((leftX + (index * (rightX - leftX)) / (dataLength - 1)) * 100) / 100;
}

/*
Пересчитать значения спектра ПМ
*/

export async function recalcConfigSpectrum(
  spectrum: any[],
  length: number,
  left: number,
  right: number,
  spectrumValueType: SpectrumYAxisType,
) {
  const params: any = {
    spectrum_data: spectrum,
    spectrum_value_type: spectrumValueType,
    length,
    left,
    right,
  };
  const {data} = await httpClient.post(`configs/recalc_spectrum`, params);
  return data;
}

/*
Пересчитать значения спектра водопада
*/

export async function recalcWaterfallSpectrum(
  spectrum: any[],
  length: number,
  left: number,
  right: number,
  spectrumValueType: SpectrumYAxisType,
) {
  const params: any = {
    spectrum_data: spectrum,
    spectrum_value_type: spectrumValueType,
    length,
    left,
    right,
  };
  const {data} = await httpClient.post(`configs/recalc_interval_spectrum`, params);
  return data;
}

/*
Получить данные спектра ПМ
*/
export async function requestConfigSpectrum(
  id: string,
  eventType: string,
  offset?: number,
  length?: number,
  timestamp?: number,
  spectrumValueType?: SpectrumYAxisType,
) {
  const params: any = {};
  // != вместо !== нужно, чтобы отсечь undefined
  if (offset != null) params.offset = offset;
  if (length != null) params.length = length;
  if (timestamp != null) params.date = timestamp;
  if (spectrumValueType != null) params.spectrum_value_type = spectrumValueType;

  params.event_type = eventType;

  const {data} = await httpClient.get(`configs/${id}/data/spectrum`, {params: params});

  if (data.spectrum_data[0][0]) {
    const leftX = data.spectrum_data[0][0][3];
    const rightX = data.spectrum_data[0][0][4];
    const dataLength: number = data.spectrum_data[0][0][1];

    const dataSet: {
      timestamp: number;
      leftX: number;
      rightX: number;
      dataLength: number;
      spectrum: {customDataIndex: number; x: number; y: number}[];
    } = {
      timestamp: data.spectrum_data[0][0][2],
      leftX,
      rightX,
      dataLength,
      spectrum: data.spectrum_data[0][0][0].map((e: any) => ({
        customDataIndex: e[1] - 1, //нельзя называть поле index, и другими, которые могут встретиться в Highchart
        x: getSpectrumFreqFormatted(e[1] - 1, leftX, rightX, dataLength),
        y: e[0],
      })),
    };
    return dataSet;
  }

  return {
    leftX: 0,
    rightX: 0,
    dataLength: 0,
    timestamp,
    spectrum: [],
  };
}

export async function requestAllConfigs() {
  const {data} = await httpClient.get(`configs/`);

  const transformData: MainConfig[] = data.map((elem: any) => ({
    type: elem.config_type,
    typeAlias: elem.config_type_alias,
    id: elem.id,
    name: elem.name,
    implemented: elem.implemented,
    node: elem.node?.id,
    nodeAlias: elem.node?.name,
  }));
  return transformData;
}

// TODO нужен другой запрос к серверу, чтобы получать конфиги программных модулей определенного узла, сейчас получаем список всех ПМ и фильтруем по id узла
export async function requestNodeConfig(nodeId: string) {
  const {data} = await httpClient.get(`configs/`);

  // TODO убрать, сделано чтобы отобразить в узле Станок программный модуль из узла Станция СОЖ
  if (nodeId === '5ef494f19ea0a36d5b00f582') {
    return data
      .filter((e: any) => e.id === '5ed77b9d9ea0a316b75f3712')
      .map((elem: any) => ({
        type: elem.config_type,
        typeAlias: elem.config_type_alias,
        id: elem.id,
        name: elem.name,
        implemented: elem.implemented,
        node: elem.node?.id,
        nodeAlias: elem.node?.name,
      }));
  }
  const transformData: MainConfig[] = data
    .filter((elem: any) => elem.node?.id === nodeId)
    .map((elem: any) => ({
      type: elem.config_type,
      typeAlias: elem.config_type_alias,
      id: elem.id,
      name: elem.name,
      implemented: elem.implemented,
      node: elem.node?.id,
      nodeAlias: elem.node?.name,
    }));
  return transformData;
}

export async function requestConfig(id: string): Promise<MainConfig> {
  const {data} = await httpClient.get(`configs/${id}/`);

  return {
    id: data.id,
    name: data.name,
    type: data.config_type,
    typeAlias: data.config_type_alias,
    implemented: data.implemented,
  };
}

export function mapConfigTypeValueInfo(elem: any) {
  return {
    alias: elem.alias,
    name: elem.name,
    phys: elem.phys,
    shortName: elem.short_name,
    unit: elem.unit,
    yAxisDesc: elem.y_axis_desc,
  };
}

export async function requestConfigsValueInfoes(
  config_ids: string[],
): Promise<{id: string; valuesInfo: ConfigTypeValueInfo[]}[]> {
  const {data} = await httpClient.get(`configs/types/values_info/`, {
    params: {
      ids: `[${config_ids.map(x => `"${x}"`).join()}]`,
    },
  });
  const transformData: {id: string; valuesInfo: ConfigTypeValueInfo[]}[] = data.map(
    (elem: any) => ({
      id: elem.config_id,
      valuesInfo: elem.values_info.map((el: any) => mapConfigTypeValueInfo(el)),
    }),
  );
  return transformData;
}

export async function requestConfigsSpectrumValueInfoes(
  configs: MainConfig[],
): Promise<{id: string; valuesInfo: ConfigTypeSpectrumValueInfo[]}[]> {
  const transformData: {id: string; valuesInfo: ConfigTypeSpectrumValueInfo[]}[] = [];
  configs.forEach(c => {
    if (c.type && spectrumValueInfoes.has(c.type)) {
      const valuesInfos = spectrumValueInfoes.get(c.type) as ConfigTypeSpectrumValueInfo[];
      transformData.push({id: c.id, valuesInfo: valuesInfos});
    }
  });
  return transformData;
}

export async function requestConfigTypeValueInfo(type: string) {
  const {data} = await httpClient.get(`configs/types/${type}/values_info/`);

  const transformData: ConfigTypeValueInfo[] = data.map((elem: any) =>
    mapConfigTypeValueInfo(elem),
  );
  return transformData;
}

export async function requestConfigTypeSpectrumValueInfo(type: ConfigType) {
  if (spectrumValueInfoes.has(type))
    return spectrumValueInfoes.get(type) as ConfigTypeSpectrumValueInfo[];
  return [];
}

export function connectionSensors() {
  const accessToken = authModule.getAccessToken || '';

  return openWebSocket(`modules/data/stats/?access_token=${accessToken}&`);
}
